import React, { useEffect, useMemo, useState } from 'react';
import useProfileCompletion from '../../../features/jobs/hooks/useProfileCompletion';
import useTowns from '../../../features/jobs/hooks/useTowns';
import { uploadProfilePhoto, updateProfile } from '../../../services/api';
import { getMediaUrl } from '../../../utils/mediaUrl';
import Button from '../ui/Button';
import CardIcon from '../icons/CardIcon';
import ProfilePhotoCropper from './ProfilePhotoCropper';
import PhoneVerification from './PhoneVerification';

const formatNationalId = (value = '') => {
  const digits = String(value).replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 6) return digits;
  if (digits.length <= 8) return `${digits.slice(0, 6)}/${digits.slice(6)}`;
  return `${digits.slice(0, 6)}/${digits.slice(6, 8)}/${digits.slice(8)}`;
};

function SectionHeading({ children, onEdit, icon }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name={icon || 'category'} />{children}</h2>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          className="text-xl leading-none text-slate-500 transition hover:text-orange-600"
          aria-label={`Edit ${children}`}
        >
          <CardIcon name="pencil" className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function EditableField({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
      />
    </div>
  );
}

function InfoValue({ label, value, fallback = 'Not specified' }) {
  const displayValue = value === null || value === undefined || value === '' ? fallback : value;

  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1 text-sm text-slate-700">{displayValue}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4 text-center">
      <p className="text-2xl font-bold text-navy">{value ?? 0}</p>
      <p className="mt-1 text-xs font-semibold text-slate-500">{label}</p>
    </div>
  );
}

const normalizeGender = (value) => {
  if (!value) return '';
  const formatted = String(value).trim();
  if (formatted.toUpperCase() === 'MALE') return 'MALE';
  if (formatted.toUpperCase() === 'FEMALE') return 'FEMALE';
  return formatted;
};

const formatGender = (value) => {
  if (!value) return 'Not specified';
  const normalized = String(value).trim().toUpperCase();
  if (normalized === 'MALE') return 'Male';
  if (normalized === 'FEMALE') return 'Female';
  return value;
};

function DashboardProfile({ role }) {
  const { profileStatus, loading, error, refreshProfile } = useProfileCompletion();
  const { towns, loading: townsLoading } = useTowns();
  const [saving, setSaving] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoCropFile, setPhotoCropFile] = useState(null);
  const [personalModalOpen, setPersonalModalOpen] = useState(false);
  const [bioEditing, setBioEditing] = useState(false);
  const [availabilityEditing, setAvailabilityEditing] = useState(false);
  const [bioDraft, setBioDraft] = useState('');
  const [availabilityDraft, setAvailabilityDraft] = useState('');
  const [savedBio, setSavedBio] = useState('');
  const [savedAvailability, setSavedAvailability] = useState('');
  const [personalDraft, setPersonalDraft] = useState({});
  const [formData, setFormData] = useState({});
  const [citySearch, setCitySearch] = useState('');
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const filteredTownOptions = towns.filter((town) => {
    const name = String(town.name || town).toLowerCase();
    const query = (citySearch || personalDraft.city || '').toLowerCase();
    return !query || name.includes(query);
  });
  const rawUser = profileStatus?.user || {};
  const personal = rawUser.personal || rawUser;
  const account = rawUser.account || {};
  const isClient = (account.role || role) === 'CLIENT';
  const identityVerified = Boolean(rawUser.verification?.identity?.verified || account.isVerified);
  const profile = useMemo(() => rawUser.profile || {}, [rawUser.profile]);
  const professional = useMemo(() => rawUser.professional || {}, [rawUser.professional]);
  const employer = useMemo(() => rawUser.employer || {}, [rawUser.employer]);
  const statistics = useMemo(
    () => (isClient ? employer.statistics : rawUser.tumaJobs?.statistics) || {},
    [employer.statistics, isClient, rawUser.tumaJobs?.statistics],
  );

  useEffect(() => {
    if (!profileStatus?.user) return;

    const nextFormData = {
      firstName: personal.firstName || '',
      middleName: personal.middleName || '',
      lastName: personal.lastName || '',
      email: personal.email || '',
      phone: personal.phone || '',
      secondaryPhone: personal.secondaryPhone || '',
      gender: normalizeGender(personal.gender),
      dateOfBirth: personal.dateOfBirth || '',
      nationalId: personal.nationalId || '',
      city: personal.location?.city || personal.city || '',
      area: personal.location?.area || personal.area || '',
      bio: profile.bio || employer.bio || professional.bio || '',
      availability: professional?.availability?.type || profile.availability || '',
      profilePhoto: personal.profilePhoto || '',
      isWhatsapp: Boolean(personal.isWhatsapp),
    };

    setFormData(nextFormData);
    setPersonalDraft(nextFormData);
    setCitySearch(nextFormData.city || '');
    setBioDraft(profile.bio || employer.bio || professional.bio || '');
    setSavedBio(profile.bio || employer.bio || professional.bio || '');
    setAvailabilityDraft(professional?.availability?.type || profile.availability || '');
    setSavedAvailability(professional?.availability?.type || profile.availability || '');
  }, [profileStatus, employer, personal, professional, profile]);

  const user = { ...personal, ...formData };
  const fullName = user.fullName || [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ') || `${role} Profile`;
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || role[0];
  const photo = getMediaUrl(user.profilePhoto || formData.profilePhoto);
  const location = [user.location?.area || user.area, user.location?.city || user.city].filter(Boolean).join(', ');

  const handlePhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setPhotoError('Please choose an image file.');
      return;
    }

    setPhotoError('');
    setPhotoCropFile(file);
  };

  const handlePhotoCrop = async (file) => {
    setSaving(true);
    try {
      const result = await uploadProfilePhoto(file);
      const photoUrl = result?.profilePhoto || result?.profile?.profilePhoto || result?.user?.profilePhoto;
      if (!photoUrl) throw new Error('Photo uploaded, but no image URL was returned.');
      setFormData((current) => ({ ...current, profilePhoto: photoUrl }));
      setPersonalDraft((current) => ({ ...current, profilePhoto: photoUrl }));
      setPhotoCropFile(null);
    } catch (uploadError) {
      setPhotoError(uploadError.message);
      throw uploadError;
    } finally {
      setSaving(false);
    }
  };

  const openPersonalEditor = () => {
    setPersonalDraft({ ...formData });
    setCitySearch(formData.city || '');
    setShowCitySuggestions(false);
    setPersonalModalOpen(true);
  };

  const closePersonalEditor = () => {
    setPersonalDraft({ ...formData });
    setCitySearch(formData.city || '');
    setShowCitySuggestions(false);
    setPersonalModalOpen(false);
  };

  const savePersonalInfo = async () => {
    const nextPersonalDraft = {
      ...personalDraft,
      nationalId: formatNationalId(personalDraft.nationalId),
    };

    const updates = {};
    Object.keys(nextPersonalDraft).forEach((field) => {
      if (nextPersonalDraft[field] !== formData[field]) {
        updates[field] = nextPersonalDraft[field];
      }
    });

    if (Object.keys(updates).length > 0) {
      await updateProfile(updates);
      await refreshProfile();
      setFormData((current) => ({ ...current, ...updates }));
    }
    setPersonalModalOpen(false);
  };

  const saveBio = async () => {
    if (bioDraft === savedBio) {
      setBioEditing(false);
      return;
    }

    await updateProfile({ bio: bioDraft });
    await refreshProfile();
    setSavedBio(bioDraft);
    setFormData((current) => ({ ...current, bio: bioDraft }));
    setBioEditing(false);
  };

  const saveAvailability = async () => {
    if (availabilityDraft === savedAvailability) {
      setAvailabilityEditing(false);
      return;
    }

    await updateProfile({ availability: availabilityDraft });
    await refreshProfile();
    setSavedAvailability(availabilityDraft);
    setFormData((current) => ({ ...current, availability: availabilityDraft }));
    setAvailabilityEditing(false);
  };

  if (loading) {
    return <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading profile...</div>;
  }

  if (error) {
    return <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">Error: {error}</div>;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl bg-white p-6 text-center shadow-sm sm:p-8">
        <div className="mx-auto flex h-28 w-28 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-2xl font-bold text-white ring-4 ring-orange-50">
          {photo ? (
            <img src={photo} alt={`${fullName} profile`} className="h-full w-full object-cover" />
          ) : (
            initials
          )}
        </div>
        <h1 className="mt-5 flex items-center justify-center gap-2 text-2xl font-bold uppercase tracking-tight text-navy sm:text-3xl"><CardIcon name={isClient ? 'briefcase' : 'user'} className="h-6 w-6 shrink-0" />{fullName}</h1>
        <p className="mt-2 text-sm font-semibold text-slate-600">{account.roleLabel || (isClient ? 'Hire Workers' : professional.title || 'Find Work')}</p>
        <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="location" />{location || 'Location not provided'}</p>
        {user.phone && <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="phone" />{user.phone}</p>}
        {user.email && <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600"><CardIcon name="mail" />{user.email}</p>}
        {user.isWhatsapp && <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CardIcon name="message" />WhatsApp</p>}
        {!isClient && (
          <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
            <CardIcon name="starFilled" className="h-4 w-4 text-orange-500" />{statistics.averageRating || 0} <span className="mx-2 text-slate-300">•</span> {statistics.totalRatings || 0} reviews
          </p>
        )}
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <label className="cursor-pointer rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            {saving ? 'Uploading...' : 'Change Photo'}
            <input type="file" accept="image/*" onChange={handlePhotoChange} disabled={saving} className="sr-only" />
          </label>
        </div>
        {photoError && <p className="mt-3 text-sm text-rose-600">{photoError}</p>}
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <SectionHeading icon="user" onEdit={openPersonalEditor}>Personal Information</SectionHeading>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <InfoValue label="Full name" value={fullName} />
          <InfoValue label="Phone" value={user.phone} />
          <InfoValue label="Secondary phone" value={user.secondaryPhone} />
          <InfoValue label="Email" value={user.email} />
          <InfoValue label="WhatsApp" value={user.isWhatsapp ? 'Yes' : 'No'} />
          <InfoValue label="Gender" value={formatGender(user.gender)} />
          <InfoValue label="Date of birth" value={user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : 'Not specified'} />
          <InfoValue label="National ID" value={user.nationalId} />
          <InfoValue label="City" value={user.city} />
          <InfoValue label="Area" value={user.area} />
          <InfoValue label="Profile photo" value={photo ? 'Available' : 'Not uploaded'} />
        </div>
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <SectionHeading icon="application" onEdit={() => { setBioDraft(savedBio); setBioEditing(true); }}>About</SectionHeading>
        {bioEditing ? (
          <div className="mt-4">
            <textarea
              value={bioDraft}
              onChange={(event) => setBioDraft(event.target.value.slice(0, 500))}
              maxLength="500"
              rows="4"
              className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
              placeholder="Tell people about yourself and the work you do"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <span>{bioDraft.length} / 500 characters</span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => { setBioDraft(savedBio); setBioEditing(false); }}>Cancel</Button>
                <Button variant="primary" onClick={saveBio}>Save</Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm leading-7 text-slate-700">{savedBio || 'No profile description yet.'}</p>
        )}
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <SectionHeading icon="check" onEdit={() => { setAvailabilityDraft(savedAvailability); setAvailabilityEditing(true); }}>Availability</SectionHeading>
        {availabilityEditing ? (
          <div className="mt-4 space-y-3">
            {[
              ['FULL_TIME', 'Full-Time'],
              ['PART_TIME', 'Part-Time'],
              ['CONTRACT', 'Contract'],
              ['ANYTIME', 'Anytime'],
            ].map(([value, label]) => (
              <label key={value} className="flex items-center gap-3 text-sm text-slate-700">
                <input
                  type="radio"
                  name="availability"
                  value={value}
                  checked={availabilityDraft === value}
                  onChange={(event) => setAvailabilityDraft(event.target.value)}
                />
                {label}
              </label>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setAvailabilityDraft(savedAvailability); setAvailabilityEditing(false); }}>Cancel</Button>
              <Button variant="primary" onClick={saveAvailability}>Save</Button>
            </div>
          </div>
        ) : (
          <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
            <CardIcon name="check" />{savedAvailability ? savedAvailability.replace('_', ' ') : 'Not specified'}
          </p>
        )}
      </section>

      {isClient && (
        <section className="rounded-3xl bg-white p-6 shadow-sm">
          <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="briefcase" />Recent Jobs</h2>
          <div className="mt-4 space-y-3">
            {employer.recentJobs?.length ? (
              employer.recentJobs.map((job) => (
                <div key={job.id} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800"><CardIcon name="check" />{job.title}</span>
                  <span className="text-sm text-slate-500">{job.status}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-slate-500">No recent jobs yet.</p>
            )}
          </div>
        </section>
      )}

      {!isClient && (
        <>
          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="category" />Skills</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {professional.skills?.length ? (
                professional.skills.map((skill) => (
                  <span key={skill.id} className="rounded-full border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-700">
                    {skill.name}
                  </span>
                ))
              ) : (
                <p className="text-sm text-slate-500">No skills added yet.</p>
              )}
            </div>
          </section>

          <section className="rounded-3xl bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="briefcase" />Tuma Jobs Experience</h2>
            <div className="mt-4 space-y-4">
              {rawUser.tumaJobs?.recentActivity?.length ? (
                rawUser.tumaJobs.recentActivity.map((item) => (
                  <div key={item.id} className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0">
                    <div>
                      <p className="inline-flex items-center gap-1.5 font-semibold text-slate-800"><CardIcon name="check" />{item.title}</p>
                      <p className="text-sm text-slate-500">
                        Completed {item.date ? new Date(item.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : 'recently'}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">{item.rating ? <><CardIcon name="starFilled" className="h-3.5 w-3.5 text-orange-500" />{item.rating}</> : ''}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No recent activity yet.</p>
              )}
            </div>
          </section>
        </>
      )}

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name={isClient ? 'eye' : 'briefcase'} />{isClient ? 'Tuma Jobs Activity' : 'Statistics'}</h2>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat label={isClient ? 'Posted' : 'Jobs Done'} value={isClient ? employer.jobsPosted : statistics.jobsCompleted} />
          <Stat label={isClient ? 'Done' : 'Reviews'} value={isClient ? statistics.jobsCompleted : statistics.totalRatings} />
          <Stat label={isClient ? 'Workers Hired' : 'Rating'} value={isClient ? statistics.workersHired : statistics.averageRating} />
        </div>
      </section>

      <section className="rounded-3xl bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 text-lg font-bold uppercase tracking-wide text-navy"><CardIcon name="shield" />Verification</h2>
        <div className="mt-4 space-y-2 text-sm">
          {!identityVerified && <PhoneVerification phone={user.phone} onAddPhone={openPersonalEditor} onVerified={refreshProfile} />}
          <p className={`inline-flex items-center gap-1.5 ${rawUser.verification?.whatsapp?.verified ? 'text-emerald-700' : 'text-slate-500'}`}>
            <CardIcon name={rawUser.verification?.whatsapp?.verified ? 'check' : 'circle'} />WhatsApp verified
          </p>
          <p className={`inline-flex items-center gap-1.5 ${rawUser.verification?.identity?.verified ? 'text-emerald-700' : 'text-slate-500'}`}>
            <CardIcon name={rawUser.verification?.identity?.verified ? 'shield' : 'circle'} />{rawUser.verification?.identity?.verified ? 'Identity verified' : 'Identity not verified'}
          </p>
        </div>
      </section>

      {personalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-xl font-bold uppercase tracking-tight text-navy">Edit Personal Information</h2>
              <button
                type="button"
                onClick={closePersonalEditor}
                className="text-2xl leading-none text-slate-400 transition hover:text-slate-700"
                aria-label="Close personal info editor"
              >
                ×
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <EditableField label="First Name" value={personalDraft.firstName} onChange={(value) => setPersonalDraft((current) => ({ ...current, firstName: value }))} />
              <EditableField label="Middle Name" value={personalDraft.middleName} onChange={(value) => setPersonalDraft((current) => ({ ...current, middleName: value }))} />
              <EditableField label="Last Name" value={personalDraft.lastName} onChange={(value) => setPersonalDraft((current) => ({ ...current, lastName: value }))} />
              <EditableField label="Email" type="email" value={personalDraft.email} onChange={(value) => setPersonalDraft((current) => ({ ...current, email: value }))} />
              <EditableField label="Phone" type="tel" value={personalDraft.phone} onChange={(value) => setPersonalDraft((current) => ({ ...current, phone: value }))} />
              <EditableField label="Secondary Phone" type="tel" value={personalDraft.secondaryPhone} onChange={(value) => setPersonalDraft((current) => ({ ...current, secondaryPhone: value }))} />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">Gender</label>
                <select
                  value={personalDraft.gender || ''}
                  onChange={(event) => setPersonalDraft((current) => ({ ...current, gender: event.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                >
                  <option value="">Select gender</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </div>
              <EditableField label="Date of Birth" type="date" value={personalDraft.dateOfBirth} onChange={(value) => setPersonalDraft((current) => ({ ...current, dateOfBirth: value }))} />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">National ID</label>
                <input
                  type="text"
                  value={personalDraft.nationalId || ''}
                  onChange={(event) =>
                    setPersonalDraft((current) => ({
                      ...current,
                      nationalId: formatNationalId(event.target.value),
                    }))
                  }
                  placeholder="123456/78/9"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">City</label>
                <div className="relative">
                  <input
                    type="text"
                    value={personalDraft.city || citySearch}
                    onFocus={() => setShowCitySuggestions(true)}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setCitySearch(nextValue);
                      setPersonalDraft((current) => ({ ...current, city: nextValue }));
                      setShowCitySuggestions(true);
                    }}
                    placeholder="Search town or city"
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                  />
                  {showCitySuggestions && (citySearch || personalDraft.city) && (
                    <div className="absolute z-10 mt-2 max-h-48 w-full overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-lg">
                      {townsLoading && <p className="px-3 py-2 text-sm text-slate-500">Loading towns...</p>}
                      {!townsLoading && filteredTownOptions.length > 0 ? (
                        filteredTownOptions.map((town) => {
                          const townName = town.name || town;
                          return (
                            <button
                              key={town.id || townName}
                              type="button"
                              onClick={() => {
                                setCitySearch(townName);
                                setPersonalDraft((current) => ({ ...current, city: townName }));
                                setShowCitySuggestions(false);
                              }}
                              className="block w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-orange-50 hover:text-slate-900"
                            >
                              {townName}
                            </button>
                          );
                        })
                      ) : (
                        <p className="px-3 py-2 text-sm text-slate-500">No matching towns</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <EditableField label="Area" value={personalDraft.area} onChange={(value) => setPersonalDraft((current) => ({ ...current, area: value }))} />
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={Boolean(personalDraft.isWhatsapp)}
                  onChange={(event) => setPersonalDraft((current) => ({ ...current, isWhatsapp: event.target.checked }))}
                />
                WhatsApp available
              </label>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-600">Profile Photo</label>
                <div className="flex items-center gap-3">
                  <input type="file" accept="image/*" onChange={handlePhotoChange} className="block w-full text-sm text-slate-600" />
                  {saving && <span className="text-sm text-slate-500">Uploading...</span>}
                </div>
                {photoError && <p className="mt-2 text-sm text-rose-600">{photoError}</p>}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={closePersonalEditor}>Cancel</Button>
              <Button variant="primary" onClick={savePersonalInfo}>Save Changes</Button>
            </div>
          </div>
        </div>
      )}

      {photoCropFile && <ProfilePhotoCropper file={photoCropFile} onCancel={() => { setPhotoCropFile(null); setPhotoError(''); }} onCrop={handlePhotoCrop} isProcessing={saving} error={photoError} />}
    </div>
  );
}

export default DashboardProfile;
